import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../api";
import AuthLayout from "./AuthLayout";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/passwordPolicy";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { token: tokenFromPath = "" } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || tokenFromPath || "";
  const emailFromLink = searchParams.get("email") || "";

  const [email, setEmail] = useState(emailFromLink);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const tokenMissing = useMemo(() => token.trim() === "", [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (tokenMissing) {
      setError("Reset token is missing. Please use the password reset link from your email.");
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }

    if (!isStrongPassword(password)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Password confirmation does not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/reset-password", {
        token,
        email: email.trim().toLowerCase(),
        password,
        password_confirmation: passwordConfirmation,
      });

      setSuccess(response?.data?.message || "Password reset successful.");
      setTimeout(() => {
        navigate("/login", {
          state: {
            loginNotice: "Password reset successful. Please log in with your new password.",
          },
        });
      }, 1200);
    } catch (requestError) {
      console.error(requestError);
      setError(requestError?.response?.data?.message || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Let’s get you back in.">
          <div className="mb-7 border-b border-white/10 pb-5 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-300">
              The Vergara Rentals
            </p>
            <h1 className="mt-2 text-2xl font-bold text-white">Reset Password</h1>
            <p className="mt-2 text-sm text-slate-300">Set your new account password.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="resetpassword-email" className="mb-2 block text-sm font-semibold text-slate-200">Email Address</label>
              <input
                type="email"
                required
                id="resetpassword-email" autoComplete="email" value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </div>

            <div>
              <label htmlFor="resetpassword-password" className="mb-2 block text-sm font-semibold text-slate-200">New Password</label>
              <input
                type="password"
                required
                id="resetpassword-password" autoComplete="new-password" value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter new password"
                className="w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
              <p className="mt-1 text-xs text-slate-400">{PASSWORD_POLICY_MESSAGE}</p>
            </div>

            <div>
              <label htmlFor="resetpassword-passwordConfirmation" className="mb-2 block text-sm font-semibold text-slate-200">Confirm Password</label>
              <input
                type="password"
                required
                id="resetpassword-passwordConfirmation" autoComplete="new-password" value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                placeholder="Confirm new password"
                className="w-full rounded-lg border border-white/10 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              />
            </div>

            <button
              type="submit"
              disabled={loading || tokenMissing}
              className="w-full rounded-lg bg-teal-600 py-3 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Resetting password..." : "Reset Password"}
            </button>

            {error ? (
              <div role="alert" className="rounded-lg border border-red-300/30 bg-red-500/15 px-4 py-3 text-sm text-red-100">
                {error}
              </div>
            ) : null}

            {success ? (
              <div role="status" className="rounded-lg border border-emerald-300/30 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-100">
                {success}
              </div>
            ) : null}
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            Return to{" "}
            <Link to="/login" className="font-semibold text-teal-300 hover:text-teal-200">
              Login
            </Link>
          </p>
    </AuthLayout>
  );
}
